// The right-hand panel for one record: inline-editable fields (each edit saves on Enter/blur and
// shows instantly) plus a History tab from the audit log. `Field` and `Section` are also used for
// sub-records (contacts, vendor links) inside a panel.
import { History, Plus, X } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { calc } from "../../../shared/calc.ts";
import type { MutationName } from "../../../shared/mutations.ts";
import { can, mutate } from "../lib/mutate.ts";
import { Badge, Button, cx, Input, Select, Textarea } from "./controls.tsx";
import { Tabs } from "./PageParts.tsx";
import { useInPane } from "./SidePane.tsx";
import { restOf, SkuInput } from "./SkuInput.tsx";
import { toast } from "./toast.tsx";

export type FieldDef = {
  key: string;
  label: string;
  type?: "text" | "textarea" | "select" | "bool" | "number" | "date" | "tags";
  options?: readonly { value: string; label: string }[];
  /** select: the empty option saves null. */
  nullable?: boolean;
  placeholder?: string;
  mono?: boolean;
  /** Shown but never editable (e.g. fields Shopify owns). */
  readOnly?: boolean;
  /** How to show the value when not editing. */
  format?: (value: unknown) => string;
  /** tags: existing tags to suggest. */
  suggestions?: string[];
  /** tags: keep the case people type (Shopify tags) instead of lowercasing. */
  keepCase?: boolean;
  /** Save this field some other way than the panel's update mutation (e.g. through Shopify). */
  save?: (value: unknown) => void;
  /** Part SKUs --- a part's SKU: its type's prefix fixed in front, only the rest edited (SkuInput). CreateDialog also
   * takes it worked out from the dialog's values (a New part's Type). */
  prefix?: string | ((values: Record<string, string>) => string);
  /** CreateDialog: a select's choices worked out from the dialog's values (a New part's Category follows its Type). No
   * choices hides the field; a value that isn't among them becomes the first. */
  optionsFor?: (values: Record<string, string>) => readonly { value: string; label: string }[];
};

export function RecordPanel({
  title,
  subtitle,
  badges,
  media,
  row,
  fields,
  update,
  entity,
  onClose,
  children,
  top,
  fieldsTitle,
}: {
  title: string;
  subtitle?: string;
  badges?: ReactNode;
  media?: ReactNode;
  row: { id: string };
  fields: FieldDef[];
  update: MutationName;
  entity: string;
  onClose: () => void;
  children?: ReactNode;
  /** Shown first, above the fields (a summary). */
  top?: ReactNode;
  /** The fields folded away at the bottom under this title, instead of first (the item panel's Shopify fields). */
  fieldsTitle?: string;
}) {
  const [tab, setTab] = useState<"details" | "history">("details");
  const editable = can(update);
  // In the side pane it fills the pane, which has the close button and the trail (ui/SidePane.tsx)
  const inPane = useInPane();
  return (
    <aside className={cx("flex h-full flex-col bg-surface", inPane ? "w-full" : "w-[480px] shrink-0 border-l border-line")}>
      <div className="flex items-start gap-3 border-b border-line px-4 py-3">
        {media}
        <div className="min-w-0 flex-1">
          <div className="truncate text-[15px] font-semibold">{title || "Untitled"}</div>
          {subtitle && <div className="truncate text-muted">{subtitle}</div>}
          {badges && <div className="mt-1.5 flex flex-wrap gap-1">{badges}</div>}
        </div>
        {!inPane && (
          <button type="button" onClick={onClose} className="rounded p-1 text-muted hover:bg-hover hover:text-text" title="Close (Esc)">
            <X className="size-4" />
          </button>
        )}
      </div>
      <Tabs
        level="record"
        tabs={[
          { key: "details", label: "Details" },
          { key: "history", label: "History" },
        ]}
        active={tab}
        onPick={setTab}
      />
      <div className="min-h-0 flex-1 overflow-y-auto">
        {tab === "details" ? (
          <div className="flex flex-col gap-0.5 p-2">
            {top}
            {!fieldsTitle && <Fields row={row} fields={fields} update={update} disabled={!editable} />}
            {children}
            {fieldsTitle && (
              <details id="sec-fields" data-sec="Fields" className="mt-3 border-t border-line pt-2">
                <summary className="cursor-pointer px-2 pb-1 text-[11px] font-medium tracking-wide text-faint uppercase hover:text-text">{fieldsTitle}</summary>
                <Fields row={row} fields={fields} update={update} disabled={!editable} />
              </details>
            )}
          </div>
        ) : (
          <HistoryList entity={entity} id={row.id} />
        )}
      </div>
    </aside>
  );
}

/** Editable fields for one row; each change runs `update` with just that field. */
export function Fields({
  row,
  fields,
  update,
  disabled,
  compact,
}: {
  row: { id: string };
  fields: FieldDef[];
  /** Not needed when every field has its own `save`. */
  update?: MutationName;
  disabled?: boolean;
  compact?: boolean;
}) {
  return fields.map((f) => (
    <Field
      key={`${row.id}:${f.key}`}
      def={f}
      compact={compact}
      value={(row as Record<string, unknown>)[f.key]}
      disabled={!!disabled || !!f.readOnly}
      onSave={(value) => (f.save ? f.save(value) : update && mutate(update, { id: row.id, patch: { [f.key]: value } } as never))}
    />
  ));
}

export function Section({
  title,
  count,
  onAdd,
  addLabel,
  children,
}: {
  title: string;
  count?: number;
  onAdd?: () => void;
  addLabel?: string;
  children: ReactNode;
}) {
  return (
    <section className="mt-3 border-t border-line pt-2">
      <div className="flex items-center gap-2 px-2 pb-1">
        <h3 className="text-[11px] font-medium tracking-wide text-faint uppercase">{title}</h3>
        {count !== undefined && <span className="text-[11px] text-faint">{count}</span>}
        {onAdd && (
          <Button size="sm" variant="ghost" className="ml-auto" icon={<Plus className="size-3.5" />} onClick={onAdd}>
            {addLabel ?? "Add"}
          </Button>
        )}
      </div>
      {children}
    </section>
  );
}

const show = (def: FieldDef, value: unknown) => {
  if (def.format) return def.format(value);
  if (def.type === "select") return def.options?.find((o) => o.value === String(value ?? ""))?.label ?? "";
  return value == null ? "" : String(value);
};

export function Field({
  def,
  value,
  disabled,
  onSave,
  compact,
}: {
  def: FieldDef;
  value: unknown;
  disabled: boolean;
  onSave: (v: unknown) => void;
  compact?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const ref = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
  useEffect(() => {
    if (editing) ref.current?.focus();
  }, [editing]);

  const label = <div className={cx("shrink-0 pt-1.5 text-muted", compact ? "w-24" : "w-32")}>{def.label}</div>;
  const row = cx("flex rounded-md px-2", disabled ? "" : "hover:bg-hover");

  if (def.type === "bool") {
    return (
      <label className={cx(row, "items-center py-1")}>
        {label}
        <input type="checkbox" disabled={disabled} checked={!!value} onChange={(e) => onSave(e.target.checked)} className="accent-[var(--accent)]" />
      </label>
    );
  }
  if (def.type === "select" && !disabled) {
    return (
      <div className={cx(row, "items-center py-0.5")}>
        {label}
        <Select
          value={String(value ?? "")}
          options={def.options ?? []}
          onChange={(e) => onSave(def.nullable && e.target.value === "" ? null : e.target.value)}
          className="border-transparent bg-transparent hover:border-line"
        />
      </div>
    );
  }
  if (def.type === "tags")
    return (
      <TagsField
        label={label}
        rowClass={row}
        value={(value as string[]) ?? []}
        disabled={disabled}
        onSave={onSave}
        suggestions={def.suggestions}
        keepCase={def.keepCase}
      />
    );

  const parse = (text: string): { ok: boolean; value?: unknown } => {
    const t = text.trim();
    if (def.type === "number") {
      if (t === "") return { ok: true, value: null };
      const n = calc(t); // a sum ("40/500") saves its result
      return n != null ? { ok: true, value: n } : { ok: false };
    }
    if (def.type === "date") return { ok: true, value: t === "" ? null : t };
    return { ok: true, value: text };
  };
  // Part SKUs --- with a prefix, the draft is only the text after it
  const prefix = typeof def.prefix === "string" ? def.prefix : "";
  const commit = () => {
    setEditing(false);
    const full = prefix ? prefix + draft.trim() : draft;
    if (full === (value == null ? "" : String(value))) return;
    const parsed = parse(full);
    if (!parsed.ok) return toast.error(`${def.label}: enter a number`);
    onSave(parsed.value);
  };
  const start = () => {
    if (disabled) return;
    const now = value == null ? "" : String(value);
    setDraft(prefix ? restOf(now, prefix) : now);
    setEditing(true);
  };

  const text = show(def, value);
  return (
    <div className={cx(row, "items-start py-0.5")}>
      {label}
      <div className="min-w-0 flex-1">
        {editing ? (
          def.type === "textarea" ? (
            <Textarea
              ref={ref}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={commit}
              onKeyDown={(e) => {
                if (e.key === "Escape") setEditing(false);
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) commit();
              }}
            />
          ) : prefix ? (
            <SkuInput
              ref={ref}
              prefix={prefix}
              value={draft}
              placeholder={def.placeholder}
              onChange={setDraft}
              onBlur={commit}
              onKeyDown={(e) => {
                if (e.key === "Escape") setEditing(false);
                if (e.key === "Enter") commit();
              }}
            />
          ) : (
            <Input
              ref={ref}
              type={def.type === "date" ? "date" : "text"}
              inputMode={def.type === "number" ? "decimal" : undefined}
              value={draft}
              placeholder={def.placeholder}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={commit}
              onKeyDown={(e) => {
                if (e.key === "Escape") setEditing(false);
                if (e.key === "Enter") commit();
              }}
            />
          )
        ) : (
          <button
            type="button"
            onClick={start}
            onFocus={start}
            tabIndex={disabled ? -1 : 0}
            className={cx(
              "min-h-8 w-full rounded-md px-2.5 py-1.5 text-left whitespace-pre-wrap",
              (def.mono || def.type === "number") && "font-mono text-[12px]",
              disabled ? "cursor-default" : "cursor-text",
            )}
          >
            {prefix && text.toUpperCase().startsWith(prefix) ? (
              <>
                <span className="text-muted">{prefix}</span>
                {text.slice(prefix.length)}
              </>
            ) : (
              text || <span className="text-faint">{disabled ? "—" : (def.placeholder ?? "Add…")}</span>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

function TagsField({
  label,
  rowClass,
  value,
  disabled,
  onSave,
  suggestions = [],
  keepCase,
}: {
  label: ReactNode;
  rowClass: string;
  value: string[];
  disabled: boolean;
  onSave: (v: unknown) => void;
  suggestions?: string[];
  keepCase?: boolean;
}) {
  const [draft, setDraft] = useState("");
  const listId = useRef(`tags-${Math.random().toString(36).slice(2)}`).current;
  const add = () => {
    const tags = draft
      .split(",")
      .map((t) => (keepCase ? t.trim() : t.trim().toLowerCase()))
      .filter(Boolean);
    setDraft("");
    if (tags.some((t) => !value.includes(t))) onSave([...value, ...tags.filter((t) => !value.includes(t))]);
  };
  return (
    <div className={cx(rowClass, "items-start py-1")}>
      {label}
      <div className="flex min-h-7 flex-1 flex-wrap items-center gap-1 px-1.5">
        {value.map((t) => (
          <span key={t} className="inline-flex h-6 items-center gap-1 rounded bg-accent-soft px-1.5 text-[12px] text-accent-text">
            {t}
            {!disabled && (
              <button type="button" className="opacity-60 hover:opacity-100" onClick={() => onSave(value.filter((x) => x !== t))} title={`Remove ${t}`}>
                <X className="size-3" />
              </button>
            )}
          </span>
        ))}
        {!disabled && (
          <>
            <input
              value={draft}
              list={listId}
              placeholder={value.length ? "" : "Add tags…"}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={add}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === ",") {
                  e.preventDefault();
                  add();
                } else if (e.key === "Backspace" && !draft && value.length) onSave(value.slice(0, -1));
              }}
              className="h-6 min-w-20 flex-1 bg-transparent text-[13px] placeholder:text-faint focus:outline-none"
            />
            <datalist id={listId}>
              {suggestions
                .filter((s) => !value.includes(s))
                .map((s) => (
                  <option key={s} value={s} />
                ))}
            </datalist>
          </>
        )}
      </div>
    </div>
  );
}

type AuditEntry = {
  id: number;
  at: string;
  via: string;
  action: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  user_name: string | null;
  user_email: string | null;
};

const verb = (action: string) => (action.endsWith(".create") ? "created this" : action.endsWith(".remove") ? "deleted this" : "changed");
const shown = (v: unknown) => (v == null || v === "" ? "empty" : typeof v === "object" ? JSON.stringify(v) : String(v));

export function HistoryList({ entity, id }: { entity: string; id: string }) {
  const [entries, setEntries] = useState<AuditEntry[] | null>(null);
  useEffect(() => {
    let live = true;
    fetch(`/api/audit?entity=${entity}&id=${id}`)
      .then((r) => r.json())
      .then((rows: AuditEntry[]) => live && setEntries(rows));
    return () => {
      live = false;
    };
  }, [entity, id]);

  if (!entries) return null;
  if (!entries.length) return <div className="p-4 text-muted">No changes recorded yet.</div>;
  return (
    <ol className="flex flex-col gap-3 p-4">
      {entries.map((e) => (
        <li key={e.id} className="flex gap-2">
          <History className="mt-0.5 size-3.5 shrink-0 text-faint" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <span className="font-medium">{e.user_name || e.user_email || "System"}</span>
              <span className="text-muted">{verb(e.action)}</span>
              {e.via !== "ui" && <Badge>{e.via}</Badge>}
              <span className="ml-auto text-[11px] text-faint">{new Date(e.at).toLocaleString()}</span>
            </div>
            {e.before && e.after && (
              <div className="mt-1 flex flex-col gap-0.5 text-[12px]">
                {Object.keys(e.after).map((k) => (
                  <div key={k} className="truncate">
                    <span className="text-muted">{k}: </span>
                    <span className="text-faint line-through">{shown(e.before?.[k])}</span> → <span>{shown(e.after?.[k])}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}
